import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { DsIconComponent } from '../icons/ds-icon.component';

export type DsPasswordState = 'default' | 'error';

@Component({
  selector: 'ds-input-password',
  standalone: true,
  imports: [DsIconComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="ds-pwd" [class.ds-pwd--error]="state === 'error'" [class.ds-pwd--disabled]="disabled"
         [attr.aria-disabled]="disabled || null">

      @if (label || hasLabelTrailingContent) {
        <div class="ds-pwd__label-row">
          @if (label) {
            <label class="ds-pwd__label" [attr.for]="inputId">{{ label }}</label>
          }
          <span class="ds-pwd__label-trailing">
            <ng-content select="[labelTrailingContent]"></ng-content>
          </span>
        </div>
      }

      <div class="ds-pwd__field-box" (click)="focusInput($event)">
        <input
          #inputControl
          class="ds-pwd__input"
          [attr.id]="inputId"
          [type]="visible ? 'text' : 'password'"
          [placeholder]="placeholder"
          [value]="value"
          [disabled]="disabled"
          [attr.maxlength]="maxCharacters ?? null"
          [attr.inputmode]="numeric ? 'numeric' : null"
          [attr.pattern]="numeric ? '[0-9]*' : null"
          [attr.autocomplete]="autocomplete || 'current-password'"
          [attr.aria-invalid]="state === 'error' ? 'true' : null"
          [attr.aria-describedby]="showHelpText && helpText ? helpId : null"
          (input)="onInput($event)"
          (focus)="focused.emit($event)"
          (blur)="blurred.emit($event)"
        />

        <button
          type="button"
          class="ds-pwd__toggle"
          [disabled]="disabled"
          [attr.aria-label]="visible ? hidePasswordLabel : showPasswordLabel"
          [attr.aria-pressed]="visible"
          (click)="toggle()"
        >
          <ds-icon [name]="visible ? 'Visibility' : 'VisibilityOff'" size="20px"></ds-icon>
        </button>
      </div>

      @if (showHelpText && helpText) {
        <span class="ds-pwd__help-text" [attr.id]="helpId"
              [attr.role]="state === 'error' ? 'alert' : null">{{ helpText }}</span>
      }

    </div>
  `,
  styles: [`
    :host { display: block; }

    .ds-pwd {
      display:        flex;
      flex-direction: column;
      gap:            var(--ds-input-gap);
      min-width:      200px;
    }

    .ds-pwd--disabled {
      opacity:        0.44;
      pointer-events: none;
    }

    .ds-pwd__label-row {
      display:     flex;
      align-items: center;
      gap:         var(--ds-input-prefix-gap);
    }

    .ds-pwd__label {
      flex:        0 1 auto;
      font-family: 'Sura Sans', system-ui, sans-serif;
      font-weight: 400;
      font-size:   var(--ds-input-label-font-size);
      line-height: var(--ds-input-label-line-height);
      color:       var(--ds-input-label-color);
    }

    .ds-pwd__label-trailing {
      display:     inline-flex;
      align-items: center;
      flex-shrink: 0;
    }

    .ds-pwd__label-trailing:empty { display: none; }

    .ds-pwd__field-box {
      display:       flex;
      align-items:   center;
      height:        var(--ds-input-field-height);
      padding:       var(--ds-input-padding-y) var(--ds-input-padding-x);
      background:    var(--ds-input-background);
      border:        var(--ds-input-border-width) solid var(--ds-input-border-color);
      border-radius: var(--ds-input-border-radius);
      transition:    border-color 120ms ease;
      box-sizing:    border-box;
      cursor:        text;
    }

    .ds-pwd--error .ds-pwd__field-box {
      background:   var(--ds-input-background-error);
      border-color: var(--ds-input-border-color-error);
    }

    .ds-pwd__field-box:focus-within {
      outline:        var(--ds-focus-stroke) solid var(--ds-focus-color);
      outline-offset: var(--ds-focus-gap);
    }

    .ds-pwd__input {
      flex:        1 1 0;
      min-width:   0;
      width:       100%;
      height:      100%;
      border:      none;
      outline:     none;
      background:  transparent;
      font-family: 'Sura Sans', system-ui, sans-serif;
      font-weight: 400;
      font-size:   var(--ds-input-field-font-size);
      line-height: var(--ds-input-field-line-height);
      color:       var(--ds-input-value-color);
      caret-color: var(--ds-input-cursor-color);
      padding:     0;
    }

    .ds-pwd__input::placeholder { color: var(--ds-input-placeholder-color); }

    .ds-pwd__toggle {
      display:          flex;
      align-items:      center;
      justify-content:  center;
      flex-shrink:      0;
      width:            var(--ds-input-eye-toggle-size);
      height:           var(--ds-input-eye-toggle-size);
      margin-left:      var(--ds-input-icon-gap);
      padding:          0;
      border:           none;
      background:       transparent;
      cursor:           pointer;
      color:            var(--ds-input-eye-toggle-color);
      border-radius:    var(--ds-input-eye-toggle-border-radius);
      transition:       color 120ms ease;
    }

    .ds-pwd__toggle:hover { color: var(--ds-input-eye-toggle-color-hover); }

    .ds-pwd__toggle:disabled { cursor: default; }

    .ds-pwd__toggle:focus-visible {
      outline:        var(--ds-focus-stroke) solid var(--ds-focus-color);
      outline-offset: var(--ds-focus-gap);
    }

    .ds-pwd__help-text {
      font-family: 'Sura Sans', system-ui, sans-serif;
      font-weight: 400;
      font-size:   var(--ds-input-help-font-size);
      line-height: var(--ds-input-help-line-height);
      color:       var(--ds-input-help-color);
    }

    .ds-pwd--error .ds-pwd__help-text {
      color: var(--ds-input-help-color-error);
    }
  `],
})
export class DsInputPasswordComponent {
  private static _counter = 0;

  @ViewChild('inputControl') private inputControl?: ElementRef<HTMLInputElement>;

  readonly inputId = `ds-input-password-${++DsInputPasswordComponent._counter}`;
  readonly helpId = `${this.inputId}-help`;

  @Input() value: string = '';
  @Input() label: string = '';
  @Input() placeholder: string = '';
  @Input() helpText: string = '';
  @Input() showHelpText: boolean = false;
  @Input() maxCharacters: number | null = null;
  @Input() state: DsPasswordState = 'default';
  @Input() disabled: boolean = false;
  @Input() numeric: boolean = false;
  @Input() autocomplete: string = 'current-password';
  @Input() hasLabelTrailingContent: boolean = false;
  @Input() showPasswordLabel: string = 'Mostrar contrasena';
  @Input() hidePasswordLabel: string = 'Ocultar contrasena';

  @Output() valueChange = new EventEmitter<string>();
  @Output() focused = new EventEmitter<FocusEvent>();
  @Output() blurred = new EventEmitter<FocusEvent>();

  visible = false;

  toggle(): void {
    if (!this.disabled) {
      this.visible = !this.visible;
    }
  }

  onInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    let nextValue = this.numeric ? input.value.replace(/\D+/g, '') : input.value;

    if (this.maxCharacters !== null) {
      nextValue = nextValue.slice(0, this.maxCharacters);
    }

    if (input.value !== nextValue) {
      input.value = nextValue;
    }

    this.value = nextValue;
    this.valueChange.emit(nextValue);
  }

  focusInput(event: MouseEvent): void {
    if (this.disabled) {
      return;
    }

    const target = event.target;
    if (target instanceof Element && target.closest('.ds-pwd__toggle')) {
      return;
    }

    this.inputControl?.nativeElement.focus();
  }
}
